import { Flex, getPixelValue } from '@lemonade-hq/uniclient-theme';
import type { Variants } from 'motion/react';
import { motion } from 'motion/react';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as styles from './HistoryMessageBubble.css';

export interface HistoryMessageBubbleProps extends PropsWithChildren {
  readonly value: string;
  readonly isBeingEdited?: boolean;
  readonly index?: number;
  readonly onClick?: (index: number) => void;
}

const HISTORY_BUBBLE_DEFAULT_ANIMATION_DURATION = 0.2;

const bubbleVariants: Variants = {
  default: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: HISTORY_BUBBLE_DEFAULT_ANIMATION_DURATION,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  editing: {
    scale: [1, 1.1, 1, 1],
    opacity: [1, 1, 1, 0],
    transition: {
      duration: HISTORY_BUBBLE_DEFAULT_ANIMATION_DURATION,
      times: [0, 0.25, 0.3, 1],
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

export const HistoryMessageBubble: React.FC<HistoryMessageBubbleProps> = ({
  children,
  value,
  isBeingEdited = false,
  index = 0,
  onClick,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [isMultiline, setIsMultiline] = useState(false);

  useEffect(() => {
    if (!measureRef.current || !value.trim()) {
      setIsMultiline(false);
      return;
    }

    const element = measureRef.current;
    const actualHeight = element.scrollHeight;
    const minHeight = getPixelValue(styles.HISTORY_MESSAGE_BUBBLE_MIN_HEIGHT);

    setIsMultiline(actualHeight > minHeight);
  }, [value, index]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick(index);
      }
    },
    [onClick, index],
  );

  return (
    <motion.div
      animate={isBeingEdited ? 'editing' : 'default'}
      className={styles.historyMessageBubble({ isMultiline })}
      initial="default"
      onClick={handleClick}
      ref={measureRef}
      variants={bubbleVariants}
    >
      <Flex>{children}</Flex>
    </motion.div>
  );
};
